import React, { useState, useEffect } from 'react';
import { Clock, Snowflake, Skull, Crown, Dog, Swords, Globe, Zap, Castle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteLock } from '../context/SiteLockContext';
import { cn } from '../lib/utils';

const EVENTS = [
    {
        id: 'winter_raid',
        name: 'Winter Raid',
        icon: Snowflake,
        color: 'cyan',
        schedules: {
            weekday: [{ start: 30, end: 45 }],
            weekend: [{ start: 30, end: 45 }, { start: 0, end: 15 }]
        }
    },
    {
        id: 'dedu_raid',
        name: 'Dedu Raid',
        icon: Skull,
        color: 'red',
        schedules: {
            weekday: [{ start: 15, end: 30 }],
            weekend: [{ start: 15, end: 30 }, { start: 45, end: 0 }]
        }
    },
    {
        id: 'giant_monarch',
        name: 'Giant Monarch Raid',
        icon: Crown,
        color: 'amber',
        schedules: {
            weekday: [{ start: 10, end: 25 }],
            weekend: [{ start: 10, end: 25 }, { start: 40, end: 55 }]
        }
    },
    {
        id: 'beast_raid',
        name: 'Beast Raid',
        icon: Dog,
        color: 'purple',
        schedules: {
            weekday: [{ start: 15, end: 30 }],
            weekend: [{ start: 15, end: 30 }, { start: 45, end: 0 }]
        }
    },
    {
        id: 'dungeons',
        name: 'Dungeons',
        icon: Swords,
        color: 'green',
        type: 'continuous',
        spawnInterval: 15,
        duration: 14
    },
    {
        id: 'world_boss',
        name: 'World Boss',
        icon: Globe,
        color: 'blue',
        schedules: {
            daily: [{ start: 0, end: 12 }, { start: 20, end: 32 }, { start: 40, end: 52 }]
        }
    },
    {
        id: 'storm_event',
        name: 'Storm Event',
        icon: Zap,
        color: 'yellow',
        type: 'ingame',
        interval: 30,
        duration: 15
    },
    {
        id: 'infernal_castle',
        name: 'Infernal Castle',
        icon: Castle,
        color: 'orange',
        schedules: {
            daily: [{ start: 15, end: 27 }, { start: 45, end: 57 }]
        }
    }
];

const getColorClasses = (color) => {
    const colors = {
        cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', ring: 'ring-cyan-500' },
        red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', ring: 'ring-red-500' },
        amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', ring: 'ring-amber-500' },
        purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', ring: 'ring-purple-500' },
        green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', ring: 'ring-green-500' },
        blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', ring: 'ring-blue-500' },
        yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', ring: 'ring-yellow-500' },
        orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', ring: 'ring-orange-500' },
    };
    return colors[color] || colors.cyan;
};

const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const EventCard = ({ event }) => {
    const { t, language } = useLanguage();
    const [now, setNow] = useState(new Date());
    const colors = getColorClasses(event.color);
    const Icon = event.icon;

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const currentMinute = now.getUTCMinutes();
    const currentSecond = now.getUTCSeconds();
    const dayOfWeek = now.getUTCDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6;

    // Calculate event status
    const getEventStatus = () => {
        if (event.type === 'continuous') {
            const cyclePosition = currentMinute % event.spawnInterval;
            const isActive = cyclePosition < event.duration;
            const timeLeft = isActive 
                ? (event.duration - cyclePosition) * 60 - currentSecond
                : (event.spawnInterval - cyclePosition) * 60 - currentSecond;
            return { isActive, timeLeft, nextStart: isActive ? 0 : timeLeft };
        }
        
        if (event.type === 'ingame') {
            return { isActive: false, description: `${event.interval} ${t('minutes')} (${t('inGameTime')})` };
        }

        const schedules = event.schedules.daily || (isWeekend ? event.schedules.weekend : event.schedules.weekday);
        
        for (const schedule of schedules) {
            const start = schedule.start;
            const end = schedule.end > schedule.start ? schedule.end : schedule.end + 60;
            const adjustedCurrent = currentMinute < start && end > 60 ? currentMinute + 60 : currentMinute;
            
            if (adjustedCurrent >= start && adjustedCurrent < end) {
                const timeLeft = (end - adjustedCurrent) * 60 - currentSecond;
                return { isActive: true, timeLeft };
            }
        }

        // Find next event
        let minWait = Infinity;
        for (const schedule of schedules) {
            let wait = schedule.start - currentMinute;
            if (wait < 0) wait += 60;
            wait = wait * 60 - currentSecond;
            if (wait < minWait) minWait = wait;
        }
        return { isActive: false, nextStart: minWait };
    };

    const status = getEventStatus();

    return (
        <div className={cn(
            "p-5 rounded-xl border transition-all duration-300",
            colors.bg, colors.border,
            status.isActive && "ring-2 ring-offset-2 ring-offset-black/80",
            status.isActive && colors.ring
        )}>
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={cn("p-2.5 rounded-lg", colors.bg)}>
                        <Icon size={24} className={colors.text} />
                    </div>
                    <div>
                        <h3 className="font-['Rajdhani'] font-bold text-lg text-white">
                            {event.name}
                        </h3>
                        {status.isActive && (
                            <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full animate-pulse", colors.bg, colors.text)}>
                                {t('active')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Timer Display */}
            <div className="space-y-3">
                {status.isActive ? (
                    <div className="p-3 rounded-lg bg-black/30">
                        <p className="text-xs text-gray-400 mb-1">{t('endsIn')}</p>
                        <p className={cn("text-2xl font-mono font-bold", colors.text)}>
                            {formatTime(status.timeLeft)}
                        </p>
                    </div>
                ) : status.nextStart !== undefined ? (
                    <div className="p-3 rounded-lg bg-black/30">
                        <p className="text-xs text-gray-400 mb-1">{t('startsIn')}</p>
                        <p className={cn("text-2xl font-mono font-bold", colors.text)}>
                            {formatTime(status.nextStart)}
                        </p>
                    </div>
                ) : status.description && (
                    <div className="p-3 rounded-lg bg-black/30">
                        <p className={cn("text-sm", colors.text)}>
                            {status.description}
                        </p>
                    </div>
                )}

                {/* Schedule Info */}
                <div className="text-xs text-gray-500 space-y-1">
                    {event.schedules?.weekday && (
                        <>
                            <p><span className="text-gray-400">{t('monToThu')}:</span> {event.schedules.weekday.map(s => `xx:${s.start.toString().padStart(2,'0')} - xx:${(s.end % 60).toString().padStart(2,'0')}`).join(', ')}</p>
                            <p><span className="text-gray-400">{t('friToSun')}:</span> {event.schedules.weekend.map(s => `xx:${s.start.toString().padStart(2,'0')} - xx:${(s.end % 60).toString().padStart(2,'0')}`).join(', ')}</p>
                        </>
                    )}
                    {event.schedules?.daily && (
                        <p><span className="text-gray-400">{t('everyDay')}:</span> {event.schedules.daily.map(s => `xx:${s.start.toString().padStart(2,'0')} - xx:${(s.end % 60).toString().padStart(2,'0')}`).join(', ')}</p>
                    )}
                    {event.type === 'continuous' && (
                        <p><span className="text-gray-400">{t('spawnRate')}:</span> {t('everyDay')} {event.spawnInterval} {t('minutes')}, {t('despawns')} {event.duration} {t('minutes')}</p>
                    )}
                    {event.type === 'ingame' && (
                        <p><span className="text-gray-400">{t('lasts')}:</span> {event.duration} {t('minutes')}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function TimerPage() {
    const { t } = useLanguage();
    const { isPageLocked } = useSiteLock();
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    if (isPageLocked('timers')) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center">
                <div className="text-center p-12 rounded-2xl bg-gradient-to-br from-red-950/50 to-red-900/30 border border-red-500/20 backdrop-blur-sm max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/30">
                        <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-3 font-['Rajdhani'] tracking-wide">{t('lockedForDev')}</h1>
                    <p className="text-gray-400 text-lg mb-2">{t('pageBeingUpdated')}</p>
                    <p className="text-gray-500 text-sm">{t('checkBackLater')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="timer-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                        <Clock className="text-amber-400" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold font-['Rajdhani'] tracking-wider text-white">
                            {t('eventSchedules')}
                        </h1>
                        <p className="text-gray-400 text-sm sm:text-base">
                            {t('eventSchedulesDescription')}
                        </p>
                    </div>
                </div>
                <div className="text-left sm:text-right">
                    <p className="text-xs text-gray-500">UTC</p>
                    <p className="text-xl sm:text-2xl font-mono font-bold text-white">
                        {currentTime.toUTCString().slice(17, 25)}
                    </p>
                </div>
            </div>

            {/* Event Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {EVENTS.map((event) => (
                    <EventCard key={event.id} event={event} />
                ))}
            </div>
        </div>
    );
}
